import type React from 'react';
import { useCallback, useEffect, useReducer, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Check, Loader2, RefreshCw, Undo2, UserX, X } from 'lucide-react';
import { fetchIdentification, type ReportViewer } from '@/lib/api';
import {
  candidates,
  identifyReducer,
  initialIdentifyState,
  playersMapping,
  reviewColumns,
  type IdentificationSegment,
  type IdentifyAction,
  type IdentifyState,
} from '@/lib/identifyPlayers';
import { playerCropToDataUri } from '@/lib/transforms';
import { cn } from '@/lib/utils';
import { assignPlayerColors } from '../tournament/playerColors';
import type { PlayerSlot } from '@/types/statistics';

const COLORS = assignPlayerColors(['player1', 'player2', 'player3', 'player4']);

/** The reference players, shown as A..D in the report's player colors. */
const REFERENCE = [1, 2, 3, 4].map((id, i) => ({
  id,
  letter: 'ABCD'[i],
  color: COLORS[`player${id}`].solid,
}));

// The flow renders outside the report, so its root opens the report's skin
// scopes itself: `report-theme` (the header slab --fip-navy, sky or black per
// skin) and `tr-root` (the --f-* fonts and --bg/--surface/--ink/... tokens,
// which matchlytics-theme.css turns black and white).
const root = 'report-theme tr-root flex h-[100dvh] flex-col overflow-hidden';
// The content column, as wide as the report's (its header's max-w-[430px]).
const column = 'mx-auto flex min-h-0 w-full max-w-[430px] flex-1 flex-col';
const title =
  'text-center [font-family:var(--f-display)] text-[36px] leading-none text-[color:var(--ink)]';
const label = '[font-family:var(--f-label)] uppercase';
const primary = cn(
  label,
  'flex min-h-[56px] items-center justify-center gap-2 rounded-[14px] bg-[color:var(--fip-navy)] px-4 text-[16px] tracking-[0.12em] text-white disabled:opacity-40',
);
const secondary = cn(
  label,
  'flex min-h-[56px] items-center justify-center gap-2 rounded-[14px] border-2 border-[color:var(--fip-navy)] bg-[color:var(--surface)] px-4 text-[16px] tracking-[0.12em] text-[color:var(--fip-navy)] disabled:opacity-40',
);
const quiet = cn(
  label,
  'flex items-center justify-center gap-1.5 rounded-[12px] border-[1.5px] border-[color:var(--hairline)] bg-[color:var(--surface)] text-[13px] tracking-[0.1em] text-[color:var(--ink-2)] disabled:opacity-40',
);

const Header: React.FC = () => {
  const { t } = useTranslation('identifyPlayers');
  return (
    <div className="flex h-14 shrink-0 items-center justify-center bg-[color:var(--fip-navy)] text-white">
      <span className={cn(label, 'text-[13px] tracking-[0.18em]')}>
        {t('title')}
      </span>
    </div>
  );
};

const Crop: React.FC<{
  src?: string;
  className?: string;
  style?: React.CSSProperties;
}> = ({ src, className, style }) => (
  <div
    className={cn('overflow-hidden bg-[color:var(--hairline-2)]', className)}
    style={style}
  >
    {src ? (
      <img
        src={playerCropToDataUri(src)}
        alt=""
        className="h-full w-full object-cover"
      />
    ) : null}
  </div>
);

const Badge: React.FC<{ label: string; color: string; className?: string }> = ({
  label: text,
  color,
  className,
}) => (
  <span
    className={cn(
      label,
      'flex items-center justify-center rounded-full text-white',
      className,
    )}
    style={{ background: color }}
  >
    {text}
  </span>
);

const YesNo: React.FC<{ onNo: () => void; onYes: () => void }> = ({
  onNo,
  onYes,
}) => {
  const { t } = useTranslation('identifyPlayers');
  return (
    <div className="grid grid-cols-2 gap-2.5">
      <button type="button" className={secondary} onClick={onNo}>
        <X className="h-5 w-5" strokeWidth={2.6} />
        {t('no')}
      </button>
      <button type="button" className={primary} onClick={onYes}>
        <Check className="h-5 w-5" strokeWidth={2.6} />
        {t('yes')}
      </button>
    </div>
  );
};

/** What who is who hands on: the players_mapping, and every kept block's crop of each reference player (A..D). */
type OnIdentified = (
  mapping: (Record<number, number> | null)[],
  crops: string[][],
) => Promise<void>;

/**
 * Who is who: the owner matches every block's players to the reference
 * block's (A..D) and leaves out blocks with other people, then the correction
 * is saved (see lib/identifyPlayers). With a single block to keep there's
 * nothing to compare, so it's saved straight away.
 */
const WhoIsWhoSteps: React.FC<{
  segments: IdentificationSegment[];
  onSave: OnIdentified;
}> = ({ segments, onSave }) => {
  const { t } = useTranslation('identifyPlayers');
  const [state, dispatch] = useReducer(
    (s: IdentifyState, a: IdentifyAction) => identifyReducer(segments, s, a),
    segments,
    initialIdentifyState,
  );
  const [saving, setSaving] = useState(false);
  const [saveFailed, setSaveFailed] = useState(false);

  const save = useCallback(async () => {
    setSaving(true);
    setSaveFailed(false);
    try {
      await onSave(
        playersMapping(segments, state),
        reviewColumns(segments, state),
      );
    } catch {
      setSaveFailed(true);
      setSaving(false);
    }
  }, [onSave, segments, state]);

  // A single block to keep: nothing to compare, save it right away.
  useEffect(() => {
    if (
      state.phase === 'review' &&
      state.order.length === 0 &&
      state.history.length === 0
    ) {
      save();
    }
    // Only on the way in.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const segment = segments[state.current];
  const row = state.assign[state.current];
  const player = REFERENCE[state.player - 1];
  const slots = state.phase === 'ask' ? candidates(segments, state) : [];
  const candidateSlot = slots[state.candidate];
  const referenceCrop =
    segments[state.reference].player_crops[`player${state.player}`];
  const position = state.order.indexOf(state.current);

  const progress = (
    <div className="flex gap-1">
      {state.order.map((block, i) => (
        <div
          key={block}
          className={cn(
            'h-1 flex-1 rounded-full',
            i < position
              ? 'bg-[color:var(--fip-navy)]'
              : i === position
                ? 'bg-[color:var(--muted-2)]'
                : 'bg-[color:var(--hairline)]',
          )}
        />
      ))}
    </div>
  );
  const undo = (
    <button
      type="button"
      onClick={() => dispatch({ type: 'undo' })}
      disabled={state.history.length === 0}
      className={cn(quiet, 'min-h-[52px] text-[14px]')}
    >
      <Undo2 className="h-[18px] w-[18px]" />
      {t('undo')}
    </button>
  );

  if (state.phase === 'ask') {
    return (
      <div className="flex min-h-0 flex-1 flex-col gap-3.5 overflow-y-auto p-4">
        {progress}
        <div className="flex justify-center gap-2.5">
          {REFERENCE.map(({ id, letter, color }) => {
            const done = row.includes(id);
            return (
              <span
                key={id}
                className={cn(
                  label,
                  'flex h-[34px] w-[34px] items-center justify-center rounded-full border-2 text-[15px] transition-transform',
                  id === state.player && !done && 'scale-110',
                )}
                style={{
                  borderColor: color,
                  background: done ? color : 'var(--surface)',
                  color: done ? 'white' : 'var(--ink)',
                }}
              >
                {letter}
              </span>
            );
          })}
        </div>
        <div className="grid grid-cols-2 gap-2.5">
          <div className="flex flex-col gap-2">
            <button
              type="button"
              className={cn(quiet, 'min-h-[44px]')}
              onClick={() => dispatch({ type: 'notPlayed', who: 'reference' })}
            >
              <UserX className="h-4 w-4" />
              {t('notPlayed')}
            </button>
            <div className="relative">
              <Crop
                src={referenceCrop}
                className="aspect-[3/4] w-full rounded-[14px] border-4"
                style={{ borderColor: player.color }}
              />
              <Badge
                label={player.letter}
                color={player.color}
                className="absolute left-2 top-2 h-9 w-9 text-[18px]"
              />
            </div>
          </div>
          <div className="flex flex-col gap-2">
            <button
              type="button"
              className={cn(quiet, 'min-h-[44px]')}
              onClick={() => dispatch({ type: 'notPlayed', who: 'candidate' })}
            >
              <UserX className="h-4 w-4" />
              {t('notPlayed')}
            </button>
            <div className="relative">
              <Crop
                src={segment.player_crops[`player${candidateSlot + 1}`]}
                className="aspect-[3/4] w-full rounded-[14px] border-4 border-[color:var(--ink)]"
              />
              <Badge
                label="?"
                color="var(--ink)"
                className="absolute left-2 top-2 h-9 w-9 text-[18px]"
              />
            </div>
          </div>
        </div>
        <h2 className={cn(title, 'mt-4')}>{t('samePerson')}</h2>
        <YesNo
          onNo={() => dispatch({ type: 'no' })}
          onYes={() => dispatch({ type: 'yes' })}
        />
        {undo}
      </div>
    );
  }

  if (state.phase === 'notPlayed') {
    const people =
      state.reason === 'all'
        ? [0, 1, 2, 3].map((slot) => segment.player_crops[`player${slot + 1}`])
        : state.reason === 'reference'
          ? [referenceCrop]
          : [segment.player_crops[`player${(state.reasonSlot ?? 0) + 1}`]];
    return (
      <div className="flex min-h-0 flex-1 flex-col gap-3.5 overflow-y-auto p-4">
        {progress}
        <div className="flex justify-center rounded-[14px] border border-[color:var(--hairline)] bg-[color:var(--surface)] p-5">
          <div
            className={cn(
              'grid w-full gap-1.5',
              people.length === 1 ? 'w-36 grid-cols-1' : 'grid-cols-4',
            )}
          >
            {people.map((crop, i) => (
              <Crop
                key={i}
                src={crop}
                className="aspect-[3/4] w-full rounded-[12px]"
              />
            ))}
          </div>
        </div>
        {/* Back here through Undo: remind them what they had answered. */}
        {state.revisiting ? (
          <p className="text-center text-[15px] leading-snug text-[color:var(--ink-2)]">
            {t(
              people.length === 1
                ? 'saidPersonNotPlayed'
                : 'saidPeopleNotPlayed',
            )}
          </p>
        ) : null}
        <h2 className={cn(title, 'mt-1')}>
          {t(people.length === 1 ? 'personPlayed' : 'peoplePlayed')}
        </h2>
        {/* No confirms they didn't play; Yes goes back to the comparison. */}
        <YesNo
          onNo={() => dispatch({ type: 'confirmNotPlayed' })}
          onYes={() => dispatch({ type: 'undo' })}
        />
      </div>
    );
  }

  if (state.phase === 'notice') {
    return (
      <div className="flex min-h-0 flex-1 flex-col p-4">
        <div className="flex flex-1 flex-col items-center justify-center gap-3.5 px-4 text-center">
          <span className="flex h-16 w-16 items-center justify-center rounded-full bg-[color:var(--fip-navy)] text-white">
            <Check className="h-8 w-8" strokeWidth={2.4} />
          </span>
          <h2 className={title}>{t('confirmed')}</h2>
          {state.notice ? (
            <p className="text-[16px] leading-snug text-[color:var(--ink-2)]">
              {t(state.notice === 'first' ? 'noticeFirst' : 'noticeLast')}
            </p>
          ) : null}
        </div>
        <button
          type="button"
          className={primary}
          onClick={() => dispatch({ type: 'continue' })}
        >
          {t('continue')}
        </button>
      </div>
    );
  }

  if (state.phase === 'empty') {
    return (
      <div className="flex min-h-0 flex-1 flex-col items-center justify-center gap-4 p-8">
        <h2 className={title}>{t('empty')}</h2>
        <div className="grid w-full grid-cols-2 gap-2">
          {undo}
          <button
            type="button"
            className={secondary}
            onClick={() => dispatch({ type: 'restart' })}
          >
            {t('restart')}
          </button>
        </div>
      </div>
    );
  }

  // Review: one column per reference player, every kept block's crop of them.
  const columns = reviewColumns(segments, state);
  return (
    <div className="flex min-h-0 flex-1 flex-col">
      <div className="flex min-h-0 flex-1 flex-col gap-3.5 overflow-y-auto p-4 pt-5">
        <div className="flex flex-col gap-2">
          <h2 className={cn(title, 'text-left')}>{t('reviewTitle')}</h2>
          <p className="text-[15px] leading-snug text-[color:var(--ink-2)]">
            {t('reviewHint')}
          </p>
        </div>
        <div className="grid grid-cols-4 gap-2">
          {REFERENCE.map(({ id, letter, color }, i) => (
            <div
              key={id}
              className="flex flex-col gap-2 rounded-[12px] border-2 bg-[color:var(--surface)] p-1.5"
              style={{ borderColor: color }}
            >
              <Badge
                label={letter}
                color={color}
                className="h-8 w-full rounded-[8px] text-[18px]"
              />
              {columns[i].map((crop, j) => (
                <Crop
                  key={j}
                  src={crop}
                  className="aspect-[3/4] w-full rounded-[8px]"
                />
              ))}
            </div>
          ))}
        </div>
        {saveFailed ? (
          <p className="text-center text-sm text-[color:var(--red)]">
            {t('failed')}
          </p>
        ) : null}
      </div>
      <div className="grid shrink-0 grid-cols-[1fr_2fr] gap-2 border-t border-[color:var(--hairline)] bg-[color:var(--surface)] p-4">
        <button
          type="button"
          className={secondary}
          disabled={saving}
          onClick={() => dispatch({ type: 'restart' })}
        >
          {t('restart')}
        </button>
        <button
          type="button"
          className={primary}
          disabled={saving}
          onClick={save}
        >
          {saving ? <Loader2 className="h-5 w-5 animate-spin" /> : null}
          {saving ? t('saving') : t('confirm')}
        </button>
      </div>
    </div>
  );
};

/**
 * The mandatory "who is who" in a match's report: loads the session's blocks,
 * walks them, and hands the players_mapping to `onConfirm` — an owner saves it,
 * a viewer who doesn't own the match previews the report with it — with each
 * player's crops, for "which one is you?".
 */
export const IdentifyPlayersFlow: React.FC<{
  sessionId: string;
  viewer: ReportViewer;
  onConfirm: OnIdentified;
}> = ({ sessionId, viewer, onConfirm }) => {
  const { t } = useTranslation('identifyPlayers');
  const [segments, setSegments] = useState<IdentificationSegment[] | null>(
    null,
  );
  const [loadFailed, setLoadFailed] = useState(false);
  const { playerId, reportToken } = viewer;

  const load = useCallback(() => {
    setLoadFailed(false);
    fetchIdentification(sessionId, { playerId, reportToken })
      .then(setSegments)
      .catch(() => setLoadFailed(true));
  }, [sessionId, playerId, reportToken]);

  useEffect(load, [load]);

  return (
    <div className={root}>
      <Header />
      {loadFailed ? (
        <div className="flex flex-1 flex-col items-center justify-center gap-4 p-8 text-center">
          <p className="text-[color:var(--muted)]">{t('loadFailed')}</p>
          <button type="button" className={secondary} onClick={load}>
            {t('retry')}
          </button>
        </div>
      ) : segments ? (
        <div className={column}>
          <WhoIsWhoSteps segments={segments} onSave={onConfirm} />
        </div>
      ) : (
        <div className="flex flex-1 items-center justify-center text-[color:var(--muted)]">
          <Loader2 className="h-6 w-6 animate-spin" />
        </div>
      )}
    </div>
  );
};

/**
 * After who is who: "which one is you?", once for the whole report — the
 * correction labelled every block with the reference players (A..D), so the
 * viewer is the same one in all of them. `onConfirm` gets their slot
 * (player1..player4): an owner saves it, anyone else keeps it in the page.
 *
 * Each player's crop can be swapped for another of theirs (one per block), so
 * a blurry or cut-off one doesn't make them hard to recognise.
 */
export const WhoAreYouStep: React.FC<{
  /** Per reference player (A..D), their crops: the first is shown first. */
  crops: string[][];
  onConfirm: (slot: PlayerSlot) => Promise<void>;
}> = ({ crops, onConfirm }) => {
  const { t } = useTranslation('identifyPlayers');
  // The same crop can come twice (blocks merged from the same one).
  const options = crops.map((list) => [...new Set(list.filter(Boolean))]);
  const [shown, setShown] = useState([0, 0, 0, 0]);
  const [selected, setSelected] = useState<number | null>(null);
  const [saving, setSaving] = useState(false);
  const [saveFailed, setSaveFailed] = useState(false);

  const confirm = async () => {
    if (selected === null) return;
    setSaving(true);
    setSaveFailed(false);
    try {
      await onConfirm(`player${selected}` as PlayerSlot);
    } catch {
      setSaveFailed(true);
      setSaving(false);
    }
  };

  return (
    <div className={root}>
      <Header />
      <div className={column}>
        <div className="flex min-h-0 flex-1 flex-col gap-4 overflow-y-auto p-4 pt-5">
          <h2 className={title}>{t('whoAreYou')}</h2>
          <div className="grid grid-cols-2 gap-3">
            {REFERENCE.map(({ id }, i) => (
              <div
                key={id}
                className={cn(
                  'relative transition-transform',
                  selected === id && 'scale-[1.03]',
                )}
              >
                <button
                  type="button"
                  aria-pressed={selected === id}
                  onClick={() => setSelected(id)}
                  className={cn(
                    'block w-full overflow-hidden rounded-[14px] border-4',
                    selected === id
                      ? 'border-[color:var(--fip-navy)] shadow-lg'
                      : 'border-transparent opacity-80',
                  )}
                >
                  <Crop
                    src={options[i][shown[i]]}
                    className="aspect-[3/4] w-full"
                  />
                </button>
                {options[i].length > 1 ? (
                  <button
                    type="button"
                    aria-label={t('otherPhoto')}
                    onClick={() =>
                      setShown((s) =>
                        s.map((n, j) =>
                          j === i ? (n + 1) % options[i].length : n,
                        ),
                      )
                    }
                    className={cn(
                      label,
                      'absolute bottom-2 right-2 flex min-h-[36px] items-center gap-1.5 rounded-full bg-black/60 px-3 text-[12px] tracking-[0.08em] text-white backdrop-blur-sm',
                    )}
                  >
                    <RefreshCw className="h-3.5 w-3.5" strokeWidth={2.6} />
                    {shown[i] + 1}/{options[i].length}
                  </button>
                ) : null}
              </div>
            ))}
          </div>
          {saveFailed ? (
            <p className="text-center text-sm text-[color:var(--red)]">
              {t('failed')}
            </p>
          ) : null}
        </div>
        <div className="shrink-0 border-t border-[color:var(--hairline)] bg-[color:var(--surface)] p-4">
          <button
            type="button"
            className={cn(primary, 'w-full')}
            disabled={selected === null || saving}
            onClick={confirm}
          >
            {saving ? <Loader2 className="h-5 w-5 animate-spin" /> : null}
            {saving ? t('saving') : t('confirm')}
          </button>
        </div>
      </div>
    </div>
  );
};
